import { useState, useEffect, useMemo } from "react";
import { SSTFApiClient } from "../services/api";

export interface UseWorkspaceUserNamesResult {
  names: Record<string, string>;
  loading: boolean;
  error: Error | null;
}

function dedupe_user_ids(userIds: (string | undefined)[]): string[] {
  const seen = new Set<string>();
  return userIds.filter((id): id is string => {
    if (id == null || id === "") return false;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export function useWorkspaceUserNames(
  apiClient: SSTFApiClient | undefined,
  workspaceId: string | undefined,
  userIds: (string | undefined)[]
): UseWorkspaceUserNamesResult {
  const [names, setNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const ids = useMemo(() => dedupe_user_ids(userIds), [userIds]);

  useEffect(() => {
    if (!apiClient || !workspaceId || ids.length === 0) {
      setNames({});
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    apiClient
      .getWorkspaceUsers(workspaceId, ids)
      .then((users) => {
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const u of users) {
          map[u.id] = u.name ?? u.id;
        }
        setNames(map);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setNames({});
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [apiClient, workspaceId, ids.join(",")]);

  return { names, loading, error };
}
